/**
 * Progress Bar Component
 * Displays execution progress with percentage
 */

interface ProgressBarProps {
  value: number | null
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'indigo'
}

const sizeClasses = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
}

const colorClasses = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  indigo: 'bg-indigo-500',
}

export function ProgressBar({
  value,
  showLabel = true,
  size = 'md',
  color = 'blue',
}: ProgressBarProps) {
  const percentage = value ?? 0
  const clampedPercentage = Math.min(Math.max(percentage, 0), 100)

  // Determine color based on progress if not specified
  const autoColor =
    percentage >= 100 ? 'green' : percentage >= 50 ? 'blue' : percentage > 0 ? 'yellow' : 'blue'

  const barColor = colorClasses[color] || colorClasses[autoColor]

  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 bg-gray-200 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${barColor} ${sizeClasses[size]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${clampedPercentage}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-sm font-medium text-gray-700 min-w-[3rem] text-right">
          {percentage.toFixed(0)}%
        </span>
      )}
    </div>
  )
}

export default ProgressBar
